import * as React from 'react';
import type {FocusControls} from 'src/FocusTrap';

export type FocusDirection = 'forward' | 'backward';

export type SubmitState<Option> = {
  query: string;
  selection: Option | null;
  focusDirection?: FocusDirection;
};

export type UseSearchPopoverProps<Option> = {
  initialQuery: string;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  search: (query: string) => Option[];
  onSubmit: (state: SubmitState<Option>) => void;
  // Whether Tab with no explicit selection auto-submits the first result —
  // the type-a-fragment-and-tab flow. Defaults to any nonempty query.
  // Pickers whose nonempty query can mean "browsing the full list" (a
  // filled category picker) opt out for that state, so tabbing through
  // commits the query instead of silently swapping in the list's first item.
  autoSubmitFirstOnTab?: (query: string) => boolean;
};

export const useSearchPopover = <Option>(
  props: UseSearchPopoverProps<Option>,
) => {
  const {initialQuery, search, onSubmit, autoSubmitFirstOnTab} = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState(initialQuery);

  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
  const searchResults = search(query);

  React.useEffect(() => {
    setSelectedIndex(null);
  }, [query]);

  React.useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  const transformIndex = (i: number) => {
    if (i < 0 || i > searchResults.length - 1) {
      return null;
    } else {
      return i;
    }
  };

  const openPopover = () => setIsOpen(true);
  const closePopover = () => setIsOpen(false);
  // For callers whose results can reorder without a query change (async
  // suggestions being prepended): an index-based highlight would silently
  // point at a different option, so they drop it when that happens. Stable
  // so callers can list it in an effect's dependencies.
  const clearSelection = React.useCallback(() => setSelectedIndex(null), []);

  const handleSelect = (index: number) => {
    setSelectedIndex(transformIndex(index));
  };

  const handleQueryChange = (value: string) => {
    setIsOpen(true);
    setQuery(value);
  };

  const handleSubmit = (
    selectedIndex: number | null,
    results: Option[],
    focusDirection?: FocusDirection,
  ) => {
    if (
      selectedIndex !== null &&
      selectedIndex >= 0 &&
      selectedIndex < results.length
    ) {
      onSubmit({query, selection: results[selectedIndex], focusDirection});
    } else {
      onSubmit({query, selection: null, focusDirection});
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (searchResults) {
      if (e.key === 'ArrowUp') {
        if (selectedIndex === null) {
          handleSelect(searchResults.length - 1);
        } else {
          handleSelect(selectedIndex - 1);
        }
      } else if (e.key === 'ArrowDown') {
        if (selectedIndex === null) {
          handleSelect(0);
        } else {
          handleSelect(selectedIndex + 1);
        }
      } else if (e.key === 'Enter') {
        if (isOpen) {
          handleSubmit(selectedIndex, searchResults);
          e.preventDefault();
        }
        // When popover is closed, allow Enter to propagate for form submission
      } else if (e.key === 'Tab') {
        // With the popover closed there is no selection to commit and no
        // mounted FocusTrap to route focus through (focusControlsRef is
        // populated by the popover) — leave Tab to the browser. Closed
        // implies unedited: any keystroke opens the popover, so there is
        // no uncommitted text to lose.
        if (!isOpen) return;
        const focusDirection: FocusDirection = e.shiftKey ? 'backward' : 'forward';
        // Auto-select first result if nothing selected
        const autoFirst = autoSubmitFirstOnTab
          ? autoSubmitFirstOnTab(query)
          : query !== '';
        if (selectedIndex === null && autoFirst) {
          handleSubmit(0, searchResults, focusDirection);
        } else {
          handleSubmit(selectedIndex, searchResults, focusDirection);
        }
        e.preventDefault();
      } else if (e.key === 'Escape') {
        handleSubmit(null, searchResults);
      }
    }
  };

  return {
    isOpen,
    query,
    selectedIndex,
    searchResults,
    handleQueryChange,
    handleKeyDown,
    openPopover,
    closePopover,
    clearSelection,
  };
};
